import {
  isDefensePosition,
  type DefensePosition,
} from "../../shared/domain/defense";
import type { BaseballStats } from "../../shared/domain/gameReducer";

export const CURRENT_KEY = "ds:current";
export const GAMES_KEY = "ds:games";
export const MIGRATION_MARKER_KEY = "ds:migrated";

const STAT_KEYS = [
  "AB",
  "H",
  "1B",
  "2B",
  "3B",
  "HR",
  "BB",
  "K",
  "RBI",
  "R",
  "SF",
] as const satisfies readonly (keyof BaseballStats)[];

type UnknownRecord = Record<string, unknown>;

interface MigrationStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface LegacyLocalData {
  current: unknown;
  games: unknown[];
}

export interface MigrationPlayerSeed {
  id: string;
  name: string;
  number: number | null;
  positionPreferences: DefensePosition[];
  active: true;
}

export interface MigrationDefenseSeed {
  assignment: Partial<Record<DefensePosition, number>>;
  sitters: number[];
  oop: number[];
}

export interface MigrationPregameSeed {
  opponentName: string;
  isHome: boolean;
  openInning: boolean;
  activePlayers: number[];
  lineup: number[];
  pitcher: number | null;
  defense?: MigrationDefenseSeed[];
}

export interface MigrationLineupSeed {
  playerIndex: number | null;
  name: string;
  number: number | null;
}

export interface ArchivedMigrationGameSeed {
  status: "archived";
  date: string | null;
  opponentName: string;
  ourRunsByInning: number[];
  opponentRunsByInning: number[];
  lineup: MigrationLineupSeed[];
  statsByPlayerIndex: Record<number, BaseballStats>;
  legacyStatsByName: Record<string, BaseballStats>;
}

export interface CurrentMigrationGameSeed {
  status: "in_progress" | "final";
  opponentName: string;
  isHome: boolean;
  openInning: boolean;
  lineup: MigrationLineupSeed[];
  defense: MigrationDefenseSeed[];
  state: {
    inning: number;
    batting: "us" | "them";
    outs: number;
    batterIndex: number;
    bases: Array<number | null>;
    ourRunsByInning: number[];
    opponentRunsByInning: number[];
    statsByPlayerIndex: Record<number, BaseballStats>;
    lastBatterIndex: number | null;
    final: boolean;
  };
}

export interface MigrationSeed {
  players: MigrationPlayerSeed[];
  pregame: MigrationPregameSeed;
  games: Array<ArchivedMigrationGameSeed | CurrentMigrationGameSeed>;
  jerseyHolder: null;
}

export interface MigrationResult {
  teamName: string;
  seed: MigrationSeed;
  legacyIdMap: Record<string, string>;
}

function asRecord(value: unknown): UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function namedRecord(
  value: unknown,
): value is UnknownRecord & { name: string } {
  const record = asRecord(value);
  return typeof record.name === "string" && Boolean(record.name.trim());
}

export function readLocalData(
  storage: MigrationStorage = localStorage,
): LegacyLocalData {
  const read = (key: string): unknown => {
    try {
      const raw = storage.getItem(key);
      if (!raw) return null;
      const value: unknown = JSON.parse(raw);
      return value && typeof value === "object" ? value : null;
    } catch {
      return null;
    }
  };
  const current = read(CURRENT_KEY);
  const gamesRaw = read(GAMES_KEY);
  return { current, games: Array.isArray(gamesRaw) ? gamesRaw : [] };
}

export function hasLocalData(
  storage: MigrationStorage = localStorage,
): boolean {
  const { current } = readLocalData(storage);
  const players = asRecord(current).players;
  return Array.isArray(players) && players.some(namedRecord);
}

export function isMigrated(storage: MigrationStorage = localStorage): boolean {
  try {
    return storage.getItem(MIGRATION_MARKER_KEY) === "1";
  } catch {
    return false;
  }
}

export function markMigrated(storage: MigrationStorage = localStorage): void {
  try {
    storage.setItem(MIGRATION_MARKER_KEY, "1");
  } catch {
    return;
  }
}

const stringValue = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim().slice(0, 80) : null;

const nonNegativeInteger = (value: unknown): number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : 0;

const integerArray = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter(
        (candidate): candidate is number =>
          typeof candidate === "number" &&
          Number.isInteger(candidate) &&
          candidate >= 0,
      )
    : [];

function sanitizeStats(value: unknown): BaseballStats {
  const stats = asRecord(value);
  const sanitized = {} as BaseballStats;
  for (const key of STAT_KEYS) sanitized[key] = nonNegativeInteger(stats[key]);
  return sanitized;
}

function sanitizePreferences(value: unknown): DefensePosition[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter(isDefensePosition))];
}

export function buildMigrationSeed(
  { current, games = [] }: LegacyLocalData,
  { newId }: { newId?: () => string | number } = {},
): MigrationResult {
  const generateId =
    typeof newId === "function" ? newId : () => crypto.randomUUID();
  const legacy = asRecord(current);
  const gameList = Array.isArray(games) ? games : [];
  const legacyPlayers = Array.isArray(legacy.players) ? legacy.players : [];
  const kept = legacyPlayers.filter(namedRecord);
  const legacyIdMap: Record<string, string> = {};
  const indexByOldId = new Map<string, number>();
  const preferences = asRecord(legacy.prefs);
  const players = kept.map((player, index): MigrationPlayerSeed => {
    const id = String(generateId());
    legacyIdMap[String(player.id)] = id;
    indexByOldId.set(String(player.id), index);
    return {
      id,
      name: player.name.trim().slice(0, 80),
      number:
        typeof player.num === "number" &&
        Number.isInteger(player.num) &&
        player.num >= 0
          ? player.num
          : null,
      positionPreferences: sanitizePreferences(preferences[String(player.id)]),
      active: true,
    };
  });

  const toIndex = (oldId: unknown): number | null =>
    indexByOldId.get(String(oldId)) ?? null;
  const mapIndexList = (value: unknown): number[] =>
    Array.isArray(value)
      ? [
          ...new Set(
            value
              .map(toIndex)
              .filter((index): index is number => index !== null),
          ),
        ]
      : [];

  const remapDefense = (value: unknown): MigrationDefenseSeed[] | null => {
    if (!Array.isArray(value)) return null;
    return value.map((candidate) => {
      const entry = asRecord(candidate);
      const assignment: Partial<Record<DefensePosition, number>> = {};
      const source = asRecord(entry.assignment);
      for (const [position, oldId] of Object.entries(source)) {
        const index = toIndex(oldId);
        if (isDefensePosition(position) && index !== null) {
          assignment[position] = index;
        }
      }
      return {
        assignment,
        sitters: mapIndexList(entry.sitters),
        oop: mapIndexList(entry.oop),
      };
    });
  };

  const activePlayers = mapIndexList(legacy.activeIds);
  const lineupCandidates = Array.isArray(legacy.lineupSlots)
    ? legacy.lineupSlots.filter((value) => value != null)
    : [];
  const lineup = mapIndexList(lineupCandidates).filter((index) =>
    activePlayers.includes(index),
  );
  let pitcher = toIndex(legacy.gamePitcher);
  if (pitcher !== null && !activePlayers.includes(pitcher)) pitcher = null;
  const pregame: MigrationPregameSeed = {
    opponentName: stringValue(legacy.oppName) ?? "Opponent",
    isHome: legacy.isHome === true,
    openInning: legacy.openInning === true,
    activePlayers,
    lineup,
    pitcher,
  };
  const pregameDefense = remapDefense(legacy.defense);
  if (pregameDefense) pregame.defense = pregameDefense;

  const nameCount = new Map<string, number>();
  players.forEach((player) =>
    nameCount.set(player.name, (nameCount.get(player.name) ?? 0) + 1),
  );
  const resolveName = (name: string): number | null => {
    if (nameCount.get(name) !== 1) return null;
    const index = players.findIndex((player) => player.name === name);
    return index >= 0 ? index : null;
  };

  const seedGames: Array<ArchivedMigrationGameSeed | CurrentMigrationGameSeed> =
    [];
  for (const candidate of gameList) {
    if (typeof candidate !== "object" || candidate === null) {
      continue;
    }
    const record = asRecord(candidate);
    const archivedLineup = Array.isArray(record.lineup)
      ? record.lineup
          .filter(
            (name): name is string =>
              typeof name === "string" && Boolean(name.trim()),
          )
          .map((name): MigrationLineupSeed => {
            const index = resolveName(name);
            const player = index === null ? null : players[index];
            return player
              ? { playerIndex: index, name, number: player.number }
              : { playerIndex: null, name, number: null };
          })
      : [];
    const statsByPlayerIndex: Record<number, BaseballStats> = {};
    const legacyStatsByName: Record<string, BaseballStats> = {};
    const sourceStats = asRecord(record.stats);
    for (const [name, stats] of Object.entries(sourceStats)) {
      const index = resolveName(name);
      if (index === null) legacyStatsByName[name] = sanitizeStats(stats);
      else statsByPlayerIndex[index] = sanitizeStats(stats);
    }
    const lineScore = asRecord(record.lineScore);
    let ourRuns = Array.isArray(record.lineScore)
      ? null
      : integerArray(lineScore.us);
    let opponentRuns = Array.isArray(record.lineScore)
      ? null
      : integerArray(lineScore.them);
    if (!ourRuns?.length) ourRuns = [nonNegativeInteger(record.ourScore)];
    if (!opponentRuns?.length) {
      opponentRuns = [nonNegativeInteger(record.oppScore)];
    }
    seedGames.push({
      status: "archived",
      date:
        typeof record.date === "string" &&
        /^\d{4}-\d{2}-\d{2}/.test(record.date)
          ? record.date.slice(0, 10)
          : null,
      opponentName: stringValue(record.opponent) ?? "Opponent",
      ourRunsByInning: ourRuns,
      opponentRunsByInning: opponentRuns,
      lineup: archivedLineup,
      statsByPlayerIndex,
      legacyStatsByName,
    });
  }

  if (detectCurrentGame(legacy)) {
    const inning = Math.max(
      1,
      typeof legacy.inning === "number" && Number.isInteger(legacy.inning)
        ? legacy.inning
        : 1,
    );
    const pad = (value: unknown): number[] => {
      const runs = integerArray(value).slice();
      while (runs.length < inning) runs.push(0);
      return runs;
    };
    const liveLineup = mapIndexList(lineupCandidates).flatMap(
      (index): MigrationLineupSeed[] => {
        const player = players[index];
        return player
          ? [
              {
                playerIndex: index,
                name: player.name,
                number: player.number,
              },
            ]
          : [];
      },
    );
    const statsByPlayerIndex: Record<number, BaseballStats> = {};
    const sourceStats = asRecord(legacy.stats);
    for (const [oldId, stats] of Object.entries(sourceStats)) {
      const index = toIndex(oldId);
      if (index !== null) statsByPlayerIndex[index] = sanitizeStats(stats);
    }
    const bases = (
      Array.isArray(legacy.bases) ? legacy.bases.slice(0, 3) : []
    ).map(toIndex);
    while (bases.length < 3) bases.push(null);
    seedGames.push({
      status: legacy.gameFinal === true ? "final" : "in_progress",
      opponentName: pregame.opponentName,
      isHome: pregame.isHome,
      openInning: pregame.openInning,
      lineup: liveLineup,
      defense: remapDefense(legacy.defense) ?? [],
      state: {
        inning,
        batting: legacy.batting === true ? "us" : "them",
        outs: Math.min(2, nonNegativeInteger(legacy.outs)),
        batterIndex: nonNegativeInteger(legacy.batterIdx),
        bases,
        ourRunsByInning: pad(legacy.ourRunsByInning),
        opponentRunsByInning: pad(legacy.oppRunsByInning),
        statsByPlayerIndex,
        lastBatterIndex: toIndex(legacy.lastBatterId),
        final: legacy.gameFinal === true,
      },
    });
  }

  return {
    teamName: stringValue(legacy.teamName) ?? "My Team",
    seed: { players, pregame, games: seedGames, jerseyHolder: null },
    legacyIdMap,
  };
}

function detectCurrentGame(current: UnknownRecord): boolean {
  const stats = asRecord(current.stats);
  const hasStats = Object.values(stats).some((value) => {
    const line = asRecord(value);
    return STAT_KEYS.some((key) => nonNegativeInteger(line[key]) > 0);
  });
  const hasRuns = (value: unknown): boolean =>
    Array.isArray(value) &&
    value.some(
      (runs) => typeof runs === "number" && Number.isInteger(runs) && runs > 0,
    );
  return (
    (typeof current.inning === "number" &&
      Number.isInteger(current.inning) &&
      current.inning > 1) ||
    (typeof current.outs === "number" &&
      Number.isInteger(current.outs) &&
      current.outs > 0) ||
    hasRuns(current.ourRunsByInning) ||
    hasRuns(current.oppRunsByInning) ||
    hasStats ||
    (Array.isArray(current.log) && current.log.length > 0) ||
    current.gameFinal === true
  );
}
